import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Button, Divider, Skeleton, Stack, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import "./translations";

import {
  SprengnetterValuationHubValuationReportModelResponseAvmPriceResponse as AvmPriceResponse,
  SprengnetterValuationHubValuationPackageModelResponseDemandScoreAvmResponse as DemandScoreResponse,
  SprengnetterValuationHubProsaWebModelResponseProsaWebResponse as ProsawebPriceResponse,
  ValuationHubCoreModelRealEstateAvmEquipmentPriceRange,
  ValuationHubCoreModelEnumsValuationType as ValuationType,
} from "@/api/generatedApi";
import { InfoIcon, MarketAnalysisIcon, SearchEyeIcon } from "@/assets";
import { CustomChip } from "@/components/customChip/CustomChip";
import { EnhancedTooltip } from "@/components/enhancedTooltip";
import { PanelHeader } from "@/components/panelHeader";
import { getAvmDisplayedRange } from "@/helpers/avm";
import { getProsaWebMarketValue } from "@/helpers/prosaWeb";
import { useModuleContext } from "@/hooks/useModuleContext";
import { colors } from "@/theme/tokens/colors";
import { PropertyInformation } from "@/types/refineRealEstate";

import { MarketPriceIndicator } from "./MarketPriceIndicator";
import { NotEnoughDataNotificationBox } from "./NotEnoughDataNotificationBox";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  panelContainer: {
    display: "grid",
    backgroundColor: "white",
    padding: "30px",
    gridTemplateRows: "min-content 1fr min-content 1fr min-content 1fr",
  },
  dataLabel: {
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "19px",
    fontFamily: "Open Sans",
  },
  responsiveContainer: {
    display: "grid",
    gridTemplateColumns: "1fr",
  },
  priceSectionContainer: {
    display: "flex",
    flexFlow: "column",
    justifyContent: "space-evenly",
    padding: "14px 20px",
  },
  infoIcon: {
    ml: 1,
    fontSize: "24px",
    cursor: "pointer",
    color: "primary.main",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    width: "100%",
  },
  analysisActionBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "20px",
    flexWrap: "wrap",
    backgroundColor: theme.semanticColors.surface.blueBg,
    borderRadius: "4px",
    padding: "20px",
    height: "88px",
  },
  analysisActionBannerText: {
    flex: "1 1 0",
  },
  analysisActionButton: {
    height: "45px",
    px: "30px",
    py: "8px",
    gap: "10px",
    borderRadius: "26px",
    flexShrink: 0,
    textTransform: "none",
    lineHeight: "19px",
  },
});

interface MarketAnalysisPanelProps {
  propertyInformation: PropertyInformation | null;
  marketPrice: ProsawebPriceResponse | null;
  priceRange: AvmPriceResponse | null;
  soneValuateIsLoading?: boolean;
  prosaWebValuateIsLoading?: boolean;
  avmValuateIsLoading?: boolean;
  onRefreshValuation?: (valuationType: ValuationType) => void;
  valuationId?: string | null;
  demandScore?: DemandScoreResponse | null;
  selectedEquipmentPriceRange?: ValuationHubCoreModelRealEstateAvmEquipmentPriceRange | null;
  readonly?: boolean;
}

export const MarketAnalysisPanel = ({
  propertyInformation,
  marketPrice,
  priceRange,
  soneValuateIsLoading,
  prosaWebValuateIsLoading,
  avmValuateIsLoading,
  onRefreshValuation,
  valuationId,
  readonly,
  selectedEquipmentPriceRange,
}: MarketAnalysisPanelProps): JSX.Element => {
  const { t } = useTranslation("MarketAnalysisPanel");
  const theme = useTheme();
  const styles = createStyles(theme);
  const location = useLocation();
  const navigate = useNavigate();
  const { valuationId: routeValuationId } = useParams<{ valuationId: string }>();
  const { isValuationReport } = useModuleContext();
  const targetValuationId = valuationId ?? routeValuationId;
  const resolvedMarketValue = getProsaWebMarketValue(marketPrice, {
    useRoundedValue: isValuationReport,
    objectType: propertyInformation?.generalInfo.objectType,
    livingArea: propertyInformation?.buildingData.livingArea,
  });
  const avmDisplayedRange = getAvmDisplayedRange(priceRange, selectedEquipmentPriceRange);
  const reportSearchParams = new URLSearchParams(location.search);
  const reportSession = reportSearchParams.get("session");
  const reportRealEstateId = reportSearchParams.get("realEstateId");
  const isAnalysisLoading = Boolean(prosaWebValuateIsLoading || avmValuateIsLoading);
  const canOpenAnalysis = isValuationReport
    ? Boolean(reportSession && reportRealEstateId)
    : Boolean(targetValuationId && marketPrice && priceRange);

  const calculatePricePerSqm = (price?: number | null, area?: number): number | undefined => {
    return price && area ? Math.round(price / area) : undefined;
  };

  const livingArea = propertyInformation?.buildingData.livingArea;

  const pricePerSquareMeter = calculatePricePerSqm(
    propertyInformation?.financialAndRentalInfo.price?.value,
    livingArea,
  );
  const marketPricePerSquareMeter = calculatePricePerSqm(resolvedMarketValue, livingArea);
  const priceRangePerSquareMeterMin = calculatePricePerSqm(avmDisplayedRange.min, livingArea);
  const priceRangePerSquareMeterMax = calculatePricePerSqm(avmDisplayedRange.max, livingArea);

  const noValuePlaceholder = "---.---";
  const noMarketValue = resolvedMarketValue == null && !prosaWebValuateIsLoading && !soneValuateIsLoading;
  const noAvmValue = !priceRange && !avmValuateIsLoading;

  return (
    <Box sx={styles.panelContainer} data-cy="market-analysis-panel">
      <PanelHeader
        title={t("marketAnalysis")}
        description={t("marketAnalysisDescription")}
        icon={
          <MarketAnalysisIcon sx={{ fontSize: theme.analysisLayout.headerIconSize, color: colors.brand.brownDark }} />
        }
      />
      <Box>
        <Box sx={{ ...styles.priceSectionContainer, height: "100%" }}>
          <Stack direction={"row"} alignItems={"center"} spacing={"15px"}>
            <Typography variant={"inputLabel"}>{t("askingPrice")}</Typography>
            <CustomChip label={t("yourPrice")} chipType="primary" />
          </Stack>
          <Stack direction={"row"} alignItems={"baseline"} spacing={"15px"}>
            <Typography variant="pageTitle">
              {propertyInformation?.financialAndRentalInfo.price?.value
                ? `${propertyInformation?.financialAndRentalInfo.price?.value?.toLocaleString("de-DE")} €`
                : t("askingPriceOnRequest")}
            </Typography>
            <Typography variant="panelDescription">
              {pricePerSquareMeter ? `${pricePerSquareMeter.toLocaleString("de-DE")} €/m²` : " "}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: "14px" }}>{t("editPriceHint")}</Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={styles.responsiveContainer}>
        <Box sx={styles.priceSectionContainer}>
          <Box sx={styles.headerRow}>
            <Stack alignItems={"start"} direction="row">
              <Typography variant={"inputLabel"}>{t("sonePrice")}</Typography>
              <EnhancedTooltip header={t("marketPriceInfo.header")} content={t("marketPriceInfo.content")}>
                <Box sx={{ display: "flex" }}>
                  <InfoIcon sx={styles.infoIcon} />
                </Box>
              </EnhancedTooltip>
            </Stack>
            {!prosaWebValuateIsLoading && !soneValuateIsLoading && (
              <MarketPriceIndicator
                propertyPrice={propertyInformation?.financialAndRentalInfo.price?.value}
                marketValue={resolvedMarketValue}
              />
            )}
            {onRefreshValuation && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => onRefreshValuation("PROSAWEB")}
                disabled={soneValuateIsLoading || prosaWebValuateIsLoading || readonly}
                sx={{ ml: "auto" }}
              >
                {t("update")}
              </Button>
            )}
          </Box>

          {!soneValuateIsLoading && !prosaWebValuateIsLoading && (
            <Stack direction={"row"} alignItems={"baseline"} spacing={"15px"}>
              <Typography variant="pageTitle" data-cy="market-analysis-market-price-value">
                {resolvedMarketValue != null
                  ? `${resolvedMarketValue?.toLocaleString("de-DE")} €`
                  : `${noValuePlaceholder} €`}
              </Typography>
              <Typography variant="panelDescription">
                {marketPricePerSquareMeter ? `${marketPricePerSquareMeter.toLocaleString("de-DE")} €/m²` : " "}
              </Typography>
            </Stack>
          )}
          {(soneValuateIsLoading || prosaWebValuateIsLoading) && (
            <Skeleton animation="wave" variant="rounded" width={210} height={40} />
          )}

          <Typography variant="panelDescription">{t("sonePriceDescription")}</Typography>
        </Box>
        <NotEnoughDataNotificationBox
          show={noMarketValue}
          header={t("missingDataError.title")}
          text={t("missingDataError.descriptionSone")}
        />
      </Box>
      <Divider />

      <Box sx={styles.responsiveContainer}>
        <Box sx={styles.priceSectionContainer}>
          <Box sx={styles.headerRow}>
            <Stack alignItems={"start"} direction="row">
              <Typography variant={"inputLabel"}>{t("marketPriceRange")}</Typography>
              <EnhancedTooltip header={t("priceRangeInfo.header")} content={t("priceRangeInfo.content")}>
                <Box sx={{ display: "flex" }}>
                  <InfoIcon sx={styles.infoIcon} />
                </Box>
              </EnhancedTooltip>
            </Stack>
            {onRefreshValuation && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => onRefreshValuation("AVM")}
                disabled={avmValuateIsLoading || readonly}
                sx={{ ml: "auto" }}
              >
                {t("update")}
              </Button>
            )}
          </Box>

          {!avmValuateIsLoading && (
            <Stack direction={"row"} alignItems={"baseline"} spacing={"15px"}>
              {priceRange && (
                <Typography variant="pageTitle" data-cy="market-analysis-price-range-value">
                  {`${avmDisplayedRange.min ? avmDisplayedRange.min.toLocaleString("de-DE") : noValuePlaceholder} - ${avmDisplayedRange.max ? avmDisplayedRange.max.toLocaleString("de-DE") : noValuePlaceholder} €`}
                </Typography>
              )}
              {!priceRange && <Typography variant="pageTitle">{`${noValuePlaceholder} €`}</Typography>}
              {priceRangePerSquareMeterMax && priceRangePerSquareMeterMin && (
                <Typography variant="panelDescription">
                  {`${priceRangePerSquareMeterMin ? priceRangePerSquareMeterMin.toLocaleString("de-DE") : " "} - ${priceRangePerSquareMeterMax ? priceRangePerSquareMeterMax.toLocaleString("de-DE") : " "} €/m²`}
                </Typography>
              )}
            </Stack>
          )}
          {avmValuateIsLoading && <Skeleton animation="wave" variant="rounded" width={210} height={40} />}
          <Typography variant="panelDescription">{t("priceWithinRangeInfo")}</Typography>
        </Box>

        <NotEnoughDataNotificationBox
          show={noAvmValue}
          header={t("missingDataError.title")}
          text={t("missingDataError.descriptionAvm")}
        />
      </Box>

      <Box sx={styles.analysisActionBanner}>
        <Typography sx={styles.analysisActionBannerText}>{t("analyzeMarketDataAndCreateReport")}</Typography>
        <Button
          variant="contained"
          color="primary"
          endIcon={<SearchEyeIcon />}
          disabled={!canOpenAnalysis || isAnalysisLoading}
          onClick={() => {
            if (isValuationReport && reportSession && reportRealEstateId) {
              navigate(
                `/valuation_report?session=${encodeURIComponent(reportSession)}&realEstateId=${encodeURIComponent(reportRealEstateId)}&view=analysis`,
              );
              return;
            }
            if (targetValuationId) {
              navigate(`/valuations/${targetValuationId}/analysis`);
            }
          }}
          sx={styles.analysisActionButton}
          data-cy="open-report-analysis-button"
        >
          {t("analyzeReport")}
        </Button>
      </Box>
    </Box>
  );
};
